import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import MaskedReveal from "./MaskedReveal";
import { mediaUrl } from "@/lib/api";

/**
 * Numbered editorial manifesto chapters. 3 chapters on ivory background.
 */
export default function Manifesto({ settings }) {
  const intro =
    settings?.intro_text ||
    "Oereshaus documents people as they are: expressive, imperfect and fully present. Based in Cape Town, we photograph portraits, weddings and celebrations using a cinematic yet honest approach.";

  const chapters = [
    {
      no: "01",
      label: "APPROACH",
      title: "Direct, then step back.",
      body: "Guided posing where it helps, and quiet distance where it doesn't. The best frames arrive between instructions.",
      image: "https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?w=1400&q=85",
    },
    {
      no: "02",
      label: "LIGHT",
      title: "Cape Town, in all its weathers.",
      body: "We work with what the day offers — cloud, southeaster, harbour light and late afternoon warmth. Nothing is smoothed away.",
      image: settings?.about_founder_portrait || "https://images.unsplash.com/photo-1532170579297-281918c8ae72?w=1400&q=85",
    },
    {
      no: "03",
      label: "STORY",
      title: "Not moments, chapters.",
      body: "Every shoot is edited as a small book: a beginning, a rise, a quiet close. Delivery feels intentional, never dumped.",
      image: "https://images.unsplash.com/photo-1509472466835-9b2ce9c6b6b9?w=1400&q=85",
    },
  ];

  return (
    <section
      data-testid="manifesto"
      className="bg-ivory text-ink py-24 md:py-40"
    >
      <div className="mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-8 mb-24">
          <div className="col-span-12 md:col-span-3">
            <p className="label-mono opacity-60 line-badge">03 — Manifesto</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <MaskedReveal
              as="p"
              lines={[intro]}
              className="font-editorial text-3xl md:text-5xl leading-[1.15] max-w-4xl"
              lineClass=""
            />
            <div className="mt-8">
              <Link to="/about" className="hover-underline label-mono" data-testid="manifesto-about-link">
                Read the full story →
              </Link>
            </div>
          </div>
        </div>

        <div className="space-y-32 md:space-y-48">
          {chapters.map((c, i) => (
            <div
              key={c.no}
              className="grid grid-cols-12 gap-4 md:gap-12 items-center"
            >
              <div className={`col-span-12 md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="photo-frame aspect-[4/5] bg-neutral-200">
                  <motion.img
                    src={mediaUrl(c.image)}
                    alt={c.title}
                    loading="lazy"
                    initial={{ scale: 1.15, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className={`col-span-12 md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="font-editorial text-6xl md:text-7xl leading-none text-ink/30">
                    {c.no}
                  </span>
                  <span className="label-mono opacity-60">{c.label}</span>
                </div>
                <h3 className="font-editorial text-4xl md:text-6xl leading-[0.95] mb-6">
                  {c.title}
                </h3>
                <p className="text-lg md:text-xl text-ink/70 leading-relaxed max-w-md">
                  {c.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
